import { format, isToday as isTodayFns, parseISO } from "date-fns";

/**
 * Date separator marker type for chat messages
 */
export interface DateSeparator {
  _type: "date_separator";
  id: string;
  dateKey: string;
  dateLabel: string;
}

/**
 * Format date label for chat messages
 * - Returns "Today" if the date is today
 * - Otherwise returns formatted date like "8 Nov 2025"
 */
export const formatChatDateLabel = (date: Date): string => {
  if (isTodayFns(date)) {
    return "Today";
  }
  // Format as "8 Nov 2025" using date-fns
  return format(date, "d MMM yyyy");
};

/**
 * Get unique date key for grouping messages
 * - Returns ISO format date string (YYYY-MM-DD)
 * - Used for comparing dates and creating unique keys
 */
export const getChatDateKey = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

/**
 * Parse message createdAt timestamp to Date object
 * - Handles both string (ISO) and Date object inputs
 */
export const parseMessageDate = (createdAt: string | Date): Date => {
  return typeof createdAt === "string"
    ? parseISO(createdAt)
    : new Date(createdAt);
};

/**
 * Type guard to check if an item is a date separator
 */
export const isDateSeparator = (item: unknown): item is DateSeparator => {
  return (
    typeof item === "object" &&
    item !== null &&
    "_type" in item &&
    (item as DateSeparator)._type === "date_separator"
  );
};

/**
 * @param messages - Chronologically sorted array of chat messages with createdAt property
 * @returns Flat array with date separators inserted at day boundaries
 */
export const insertDateSeparators = <
  T extends { id: string; createdAt: string | Date },
>(
  messages: T[]
): (T | DateSeparator)[] => {
  if (messages.length === 0) return [];

  const result: (T | DateSeparator)[] = [];
  let lastDateKey = "";

  // Single pass through messages - O(n)
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    const messageDate = parseMessageDate(message.createdAt);
    const dateKey = getChatDateKey(messageDate);

    // Insert separator when date changes
    if (dateKey !== lastDateKey) {
      result.push({
        _type: "date_separator",
        id: `separator-${dateKey}`,
        dateKey,
        dateLabel: formatChatDateLabel(messageDate),
      });
      lastDateKey = dateKey;
    }

    result.push(message);
  }

  return result;
};
